<template>
  <div class="container-fluid">
    <h1 class="mb-3">SIM Management</h1>
    <div v-if="auth.isAdmin" class="row mb-3 align-items-center">
      <AddActions view="SIM" @new="newModal"/>
      <div class="col-auto">
        <button type="button" class="btn btn-sm btn-light" 
            @click="csvModal" 
            v-tooltip
            data-bs-toggle="tooltip" 
            title="Import from CSV"
        >
          <img type="png" src="@/assets/icons/csv.png" alt="Import CSV" style="height: 20px;" />
        </button>
      </div>
      <div v-if="auth.isAdmin" class="col d-flex justify-content-end">
        
        <ToggleLockedButton 
          v-model="includeLocked" 
          class="me-2"
          @toggle="toggleLockedFilter"
        />
        
        <SimSyncControl 
          :endpoint="simUrl" 
          @syncComplete="syncComplete" 
        />
        
      </div>
    </div>
    <div>
      <SimForm v-if="modalNew || modalDetail || modalEdit" 
          :tag="`sim`" :msg="`${modalMsg} SIM`" :schema="entitySchema"
          :schemaTypes="schemaTypes" :parentObj="editSim" 
          @formDataSubmitted="handleSave" 
          @formClosed="closedModal" 
      />
      <ActForm v-if="modalAct" 
          :simDetail="editSim" 
          :validApns="provisioningApns"
          :serviceOptions="serviceTypes" 
          @formDataSubmitted="provisionSim" 
          @formClosed="closedModal" 
      />
      <CsvForm v-if="modalCsv" :tag="`sim`" :msg="`Import SIM List`" 
          :schema="entitySchema" :types="customDbTypes"
          @formDataSubmitted="importCsv" 
          @formClosed="closedModal" 
      />
    </div>
    <div>
      <SimTable 
        modelName="SIM"
        :columns="columns" 
        :options="tableOptions" 
        :ajaxUrl="simUrl" 
        :queryParams="simQueryParams"
        ref="table"
      >
        <template #provision="row">
          <div v-if="auth.isAdmin" class="form-check form-switch d-flex justify-content-center">
            <input 
              class="form-check-input" 
              type="checkbox" 
              role="switch" 
              :checked="row.rowData.state === 'ACTIVATED'"
              @change="provisionPrep(row.rowData)"
              v-tooltip
              :title="row.rowData.state === 'ACTIVATED' ? 'Deactivate' : 'Activate'"
            >
          </div>
        </template>
        <template #details="row">
          <TableActions mode="details" :rowData="row.rowData" @detail="detail" />
        </template>
        <template #actions="row">
          <TableActions mode="actions" :rowData="row.rowData" @edit="edit" @remove="remove" />
        </template>
      </SimTable>
    </div>
  </div>
</template>

<script setup>
import { reactive, ref, computed, onMounted, nextTick } from 'vue';
import { useAuthStore } from '@/stores/authStore';
import { useSchemaStore } from '@/stores/schemaStore';
import { ApiEndpoint, isProd, isMockData } from '../utils/config';
import { getCommonColumns, renderTime } from '@/utils/table';
import { handleRowSave, handleRowDelete } from '@/utils/form';
import { apiFetch } from '@/utils/api';

import SimTable from '@/components/TableComponent.vue';
import TableActions from '@/components/TableActions.vue';
import AddActions from '@/components/AddActions.vue';
import SimForm from '@/components/DataFormModal.vue';
import ActForm from '@/components/ActivationFormModal.vue';
import CsvForm from '@/components/CsvUploadModal.vue';
import ToggleLockedButton from '@/components/ToggleLockedButton.vue';
import SimSyncControl from '@/components/SyncComponent.vue';

// --- Reactive State and Injections ---
const auth = useAuthStore();
const schema = useSchemaStore();
const table = ref(); // Template ref for DataTable component

// Data properties
const entitySchema = ref([]);  // entity schemas are an array of objects
const schemaTypes = reactive({});
const provisioningApns = ref([]);
const customDbTypes = reactive({}); // Added to match the CsvForm prop binding
const extraQueryParams = ref({});

const includeLocked = ref(false);

// Modal state (boolean refs)
const modalNew = ref(false);
const modalEdit = ref(false);
const modalDetail = ref(false);

const modalAct = ref(false);
const modalCsv = ref(false);

// Object state (reactive objects)
const editSim = reactive({});
let _schemaSnapshot = null;
const newWritableFields = ['iccid', 'imsi', 'homeNetwork'];
// const csvSims = reactive({}); // Removed as it's not used in the provided methods

// --- Computed Properties ---
const modalMsg = computed(() => {
  if (modalNew.value) return 'Add';
  if (modalDetail.value) return 'View';
  if (modalEdit.value) return 'Edit';
  return 'View';
});

const serviceTypes = computed(() => schema.getEnumValues('DeviceClassType'));

const renderApnList = (data, type, row) => {
    if (Array.isArray(data) && data.length > 0) {
      return `[ ${data.join(', ')} ]`; // Join list items with a comma
    }
    return '<span class="text-muted fst-italic">[-]</span>'; // Indicator for empty list
};

const columns = [
  { title: 'ICCID', data: 'iccid', width: '15%', className: 'td-truncate' },
  { title: 'IMSI', data: 'imsi', width: '10%' },
  { title: 'Customer', data: 'customerId', width: '13%' },
  { title: 'State', data: 'state', width: '8%', className: 'text-center' },
  { title: 'State Time', data: 'stateTime', width: '13%', className: 'td-truncate', render: renderTime },
  { title: 'NIDD APNs', data: 'niddApnList', width: '14%', 
    className: 'td-truncate text-center', render: renderApnList },
  { title: 'IP APNs', data: 'ipApnList', width: '8%', 
    className: 'td-truncate text-center', render: renderApnList },
  { title: 'Note', data: 'note', className: 'td-truncate' },

  { title: 'Provision', data: 'state', width: '8%', render: '#provision',
    orderable: false, searchable: false, visible: auth.isAdmin },
  ...getCommonColumns(auth.isAdmin),
];

const tableOptions = computed(() => {
  return {
    fixedColumns: { left: 1, right: auth.isAdmin ? 3 : 1 },
  };
});

const simUrl = ApiEndpoint.SIMS;
const simQueryParams = computed(() => {
  const params = {
    includeLocked: includeLocked.value ? includeLocked.value : undefined,
  };
  return { ...params, ...extraQueryParams.value };
});

// Methods

const resetSim = (simDetail) => {
  for (const k of Object.keys(simDetail)) {
    simDetail[k] = null;
  }
};

const closedModal = async () => {
  modalNew.value = false;
  modalDetail.value = false;
  modalEdit.value = false;
  modalAct.value = false;
  modalCsv.value = false;
  if (_schemaSnapshot) {
    for (const [i, field] of entitySchema.value.entries()) {
      entitySchema.value[i] = _schemaSnapshot[i];
    }
    _schemaSnapshot = null;
  }
};

const newModal = () => {
  resetSim(editSim);
  _schemaSnapshot = JSON.parse(JSON.stringify(entitySchema.value));
  for (const [i, field] of entitySchema.value.entries()) {
    if (newWritableFields.includes(field.name)) {
      entitySchema.value[i].isReadOnly = false;
    }
  }
  editSim.state = schemaTypes.value.SimStateType[0];
  modalNew.value = true;
};

const csvModal = () => {
  modalCsv.value = true;
  // csvSims = {}; // Handled by reactive object definition if needed
};

const loadSims = async (forceRefresh = false) => {
  await nextTick();
  // Force DataTables to read parent boundaries and execute its first clean server network fetch
  if (table.value) {
    await table.value.reloadTable(forceRefresh);
  }
};

const loadProvisioningApns = async () => {
  let apns = [];
  const url = ApiEndpoint.APNS;
  const queryParams = [];
  const query = queryParams.length > 0 ? ('?' + queryParams.join('&')) : '';
  if (!isProd && isMockData) {
    console.warn(`Dev Mode: Using mock APN data`);
    ({ default: apns } = await import('@/mocks/test_apns.json').catch(err => {
      console.error(`No mock APN data found`);
    }));
  } else {
    try {
      ({ items: apns } = await apiFetch(`${url}${query}`));
    } catch (err) {
      console.error(`API fetch error:`, err);
    }
  }
  const exclude = [];
  if (!auth.isAdmin) {
    exclude.push('inmarsat.demo', 'viasat.mock.nidd', 'skylo.ip');
  }
  provisioningApns.value = apns
    .filter(apn => apn.parentId === null || apn.parentId === apn.id)
    .filter(apn => !exclude.includes(apn.id))
    .map(apn => `${apn.id} (${apn.type})`);
  console.debug(`${provisioningApns.value.length} Available` +
    ` provisioning APNs: ${provisioningApns.value}`);
};

const syncComplete = async() => {
  await loadSims();
};

const toggleLockedFilter = async () => {
  await loadSims(true);
};

const handleSave = async (simDetail) => {
  try {
    await handleRowSave('sim', simDetail, { ...editSim });
    closedModal();
    await loadSims(true);
  } catch (err) {
    console.error(err);
  }
};

const detail = (row) => {
  Object.assign(editSim, row);
  modalDetail.value = true;
};

const edit = (simDetail) => {
  Object.assign(editSim, simDetail); // populate the reactive object
  modalEdit.value = true; // activate the form
};

const remove = async (simDetail) => {
  const confirmKeys = ['customerId', 'iccid', 'imsi'];
  await handleRowDelete('sim', simDetail, confirmKeys);
  await loadSims(true);
};

const provisionPrep = (simDetail) => {
  const isActivated = simDetail.state === 'ACTIVATED';
  const action = isActivated ? "DEACTIVATING" : "ACTIVATING";
  const confirmed = confirm(
    `${action} ${simDetail.imsi} may incur charges.` +
    `\nDO YOU WANT TO CONTINUE?`
  );
  if (confirmed) {
    Object.assign(editSim, simDetail);
    modalAct.value = true;
  }
};

const provisionSim = async (provisioningMeta) => {
  const { operation, sim, serviceType } = provisioningMeta;
  closedModal();
  try {
    const provisioningMeta = { iccid: sim.iccid };
    if (operation === 'ACTIVATE') {
      provisioningMeta.ipApnList = sim.ipApnList;
      provisioningMeta.niddApnList = sim.niddApnList;
      provisioningMeta.serviceType = serviceType;
    }
    const endpointMap = {
      'ACTIVATE': '/activate',
      'DEACTIVATE': '/deactivate',
      // 'UPDATE': '/update',
    };
    const endpoint = ApiEndpoint.SIMS + endpointMap[operation];
    const [updatedSim] = await apiFetch(endpoint, {
      method: 'POST',
      body: provisioningMeta,
    });
    const dt = table.value.dt;
    const row = dt.row((idx, data) => data.iccid === updatedSim.iccid);
    if (row.any()) {
      // Update the data for that row and redraw only that row
      row.data(updatedSim).draw(false);
      console.debug(`Row ${updatedSim.iccid} updated locally.`);
    } else {
      // Fallback if row wasn't found in current view
      dt.ajax.reload(null, false);
    }
  } catch (err) {
    console.error(`SIM provisioning failed: ${err.message}`);
    table.value.dt.draw(false);
  }
};

const importCsv = async (importData) => {
  if (Object.hasOwn(importData, 'reason')) {
    console.error(`Import failed: ${importData.reason} (row ${importData.row})`);
    return;
  }
  console.info(`Imported ${importData.length} rows from file`);
  closedModal();
  try {
    const result = await apiFetch(ApiEndpoint.SIMS, {
      method: 'POST',
      body: importData,
    });
    console.debug(`Import CSV result: ${JSON.stringify(result)}`);
    await loadSims(true);
  } catch (err) {
    console.error(`Failed to import CSV: ${err.message}`);
  }
};

onMounted(async () => {
  entitySchema.value = schema.getFieldMeta('sim');
  schemaTypes.value = schema.getModelEnums('sim');
  await loadProvisioningApns();
});
</script>

<style>
.DTFC_LeftWrapper, .DTFC_RightWrapper {
  background-color: white;
  z-index: 10;
}
</style>